import { App as AntdApp, Button, Tag, Typography } from 'antd';
import {
	ClearOutlined,
	CheckOutlined,
	CustomerServiceOutlined,
	FileTextOutlined,
	GlobalOutlined,
	InfoCircleOutlined,
	LogoutOutlined,
	MessageOutlined,
	LeftOutlined,
	RightOutlined,
	SafetyCertificateOutlined,
	UserOutlined,
} from '@ant-design/icons';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import webPackage from '../../package.json';
import LocaleSwitcher, { LOCALE_OPTIONS, normalizeLocaleValue } from '../components/LocaleSwitcher.js';
import MobileSubPageScaffold, { SubPageSection } from '../components/MobileSubPageScaffold.js';
import { ActionList } from '../components/MobileRootScaffold.js';
import { useAuthStore } from '../stores/auth.js';
import { useRegion } from '../hooks/useRegion.js';
import { isCapacitor } from '../utils/api-base.js';
import { clearRecentRecords, getRecentRecords } from '../utils/recent-records.js';

export default function Settings() {
	const region = useRegion();
	return region === 'apac' ? <NativeSettings /> : <SettingsClassic />;
}

function SettingsClassic() {
	const { t, i18n } = useTranslation();
	const navigate = useNavigate();
	const region = useRegion();
	const clearAuth = useAuthStore((s) => s.clearAuth);
	const user = useAuthStore((s) => s.user);
	const { message } = AntdApp.useApp();
	const [recentCount, setRecentCount] = useState(() => getRecentRecords().length);

	const runtime = useMemo(() => (isCapacitor() ? t('settings.runtime_app') : t('settings.runtime_web')), [t]);

	const clearRecent = () => {
		clearRecentRecords();
		setRecentCount(0);
		message.success(t('settings.clear_recent_success'));
	};

	const logout = () => {
		clearAuth();
		navigate('/login', { replace: true });
	};

	return (
		<MobileSubPageScaffold title={t('mtab.profile.settings')} onBack={() => navigate(-1)}>
			<SubPageSection title={t('settings.account')}>
				<ActionList
					items={[
						{
							key: 'profile',
							title: t('mtab.profile.edit_profile'),
							subtitle: user?.account || t('mtab.profile.guest'),
							icon: <UserOutlined />,
							onClick: () => navigate('/m/profile-edit'),
						},
						{
							key: 'language',
							title: t('root.account.language'),
							subtitle: t('root.account.language_hint'),
							icon: <GlobalOutlined />,
							trailing: <LocaleSwitcher />,
						},
					]}
				/>
			</SubPageSection>

			<SubPageSection title={t('settings.app')}>
				<ActionList
					items={[
						...(region === 'cn' ? [{
							key: 'region',
							title: t('root.account.region'),
							icon: <SafetyCertificateOutlined />,
							trailing: <Tag color="default" style={{ marginInlineEnd: 0 }}>CN</Tag>,
						}] : []),
						{
							key: 'runtime',
							title: t('settings.runtime'),
							icon: <InfoCircleOutlined />,
							trailing: <Typography.Text type="secondary">{runtime}</Typography.Text>,
						},
						{
							key: 'version',
							title: t('settings.version'),
							subtitle: t('settings.version_hint'),
							icon: <FileTextOutlined />,
							trailing: <Typography.Text type="secondary">v{webPackage.version}</Typography.Text>,
						},
					]}
				/>
			</SubPageSection>

			<SubPageSection title={t('settings.storage')}>
				<ActionList
					items={[
						{
							key: 'clear_recent',
							title: t('settings.clear_recent'),
							subtitle: t('settings.clear_recent_hint', { count: recentCount }),
							icon: <ClearOutlined />,
							onClick: clearRecent,
						},
					]}
				/>
			</SubPageSection>

			<SubPageSection title={t('settings.support')}>
				<ActionList
					items={[
						{
							key: 'feedback',
							title: t('mtab.profile.feedback'),
							subtitle: t('settings.feedback_hint'),
							icon: <MessageOutlined />,
							onClick: () => navigate('/m/feedback'),
						},
						{
							key: 'hotline',
							title: t('mtab.profile.hotline'),
							subtitle: t('settings.hotline_hint'),
							icon: <CustomerServiceOutlined />,
							onClick: () => navigate('/m/hotline'),
						},
					]}
				/>
			</SubPageSection>

			<Button
				danger
				block
				icon={<LogoutOutlined />}
				onClick={logout}
				style={{ marginTop: 12, height: 44, borderRadius: 8, borderColor: '#f2b8b5', color: '#b42318' }}
			>
				{t('settings.logout')}
			</Button>
		</MobileSubPageScaffold>
	);
}

function NativeSettings() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const clearAuth = useAuthStore((s) => s.clearAuth);
	const [cacheSize, setCacheSize] = useState(() => estimateNativeCacheSize());
	const [confirmClearOpen, setConfirmClearOpen] = useState(false);

	const logout = () => {
		clearAuth();
		navigate('/login', { replace: true });
	};

	const clearCacheNow = () => {
		clearRecentRecords();
		try {
			localStorage.removeItem('ulucu.album');
		} catch {
			/* ignore */
		}
		setCacheSize('0KB');
	};

	const clearCache = () => {
		setConfirmClearOpen(true);
	};

	return (
		<div style={{ background: '#f5f5f5', minHeight: '100vh' }}>
			<NativeHeader title={t('native.settings.title')} onBack={() => navigate(-1)} />
			<div style={{ background: '#fff', borderTop: '10px solid #f5f5f5' }}>
				<NativeSettingRow title={t('native.settings.modify_password')} onClick={() => navigate('/m/settings/password')} />
				<NativeSettingRow title={t('native.settings.language_setting')} onClick={() => navigate('/m/settings/language')} />
				<NativeSettingRow title={t('native.settings.clear_cache')} trailing={cacheSize} onClick={clearCache} />
				<NativeSettingRow title={t('native.settings.about_us')} onClick={() => navigate('/m/settings/about')} />
			</div>
			<button
				type="button"
				onClick={logout}
				style={{
					width: '100%',
					height: 62,
					marginTop: 12,
					border: 0,
					background: '#fff',
					color: '#20242b',
					fontSize: 17,
					cursor: 'pointer',
				}}
			>
				{t('native.settings.logout')}
			</button>
			{confirmClearOpen && (
				<NativeConfirmDialog
					title={t('native.settings.clear_cache')}
					content={t('native.settings.clear_cache_confirm')}
					cancelText={t('native.common.cancel')}
					confirmText={t('native.common.confirm')}
					onCancel={() => setConfirmClearOpen(false)}
					onConfirm={() => {
						clearCacheNow();
						setConfirmClearOpen(false);
					}}
				/>
			)}
		</div>
	);
}

function NativeConfirmDialog({
	title,
	content,
	cancelText,
	confirmText,
	onCancel,
	onConfirm,
}: {
	title: string;
	content: string;
	cancelText: string;
	confirmText: string;
	onCancel: () => void;
	onConfirm: () => void;
}) {
	return (
		<div
			role="presentation"
			style={{
				position: 'fixed',
				top: 0,
				right: 0,
				bottom: 0,
				left: 0,
				zIndex: 1000,
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				background: 'rgba(0, 0, 0, 0.35)',
				padding: 28,
			}}
			onClick={onCancel}
		>
			<div
				role="dialog"
				aria-modal="true"
				aria-label={title}
				onClick={(event) => event.stopPropagation()}
				style={{
					width: '100%',
					maxWidth: 320,
					background: '#fff',
					borderRadius: 8,
					overflow: 'hidden',
					boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
				}}
			>
				<div style={{ padding: '20px 22px 16px', textAlign: 'center' }}>
		<div style={{ fontSize: 15, fontWeight: 500, color: '#20242b', marginBottom: 10 }}>{title}</div>
					<div style={{ fontSize: 15, lineHeight: 1.55, color: '#5b6470' }}>{content}</div>
				</div>
				<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', borderTop: '1px solid #eceef2' }}>
					<button
						type="button"
						onClick={onCancel}
						style={{
							height: 48,
							border: 0,
							borderRight: '1px solid #eceef2',
							background: '#fff',
							color: '#5b6470',
							fontSize: 16,
						}}
					>
						{cancelText}
					</button>
					<button
						type="button"
						onClick={onConfirm}
						style={{
							height: 48,
							border: 0,
							background: '#fff',
							color: '#ff8a2b',
							fontSize: 16,
							fontWeight: 600,
						}}
					>
						{confirmText}
					</button>
				</div>
			</div>
		</div>
	);
}

export function NativePasswordSettings() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [oldPassword, setOldPassword] = useState('');
	const [newPassword, setNewPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	return (
		<div style={{ background: '#f5f5f5', minHeight: '100vh' }}>
			<NativeHeader title={t('native.settings.modify_password')} onBack={() => navigate(-1)} action={t('native.common.save')} />
			<div style={{ background: '#fff', borderTop: '10px solid #f5f5f5' }}>
				<NativeInputRow label={t('native.settings.old_password')} value={oldPassword} onChange={setOldPassword} />
				<NativeInputRow label={t('native.settings.new_password')} value={newPassword} onChange={setNewPassword} />
				<NativeInputRow label={t('native.settings.confirm_password')} value={confirmPassword} onChange={setConfirmPassword} />
			</div>
		</div>
	);
}

export function NativeLanguageSettings() {
	const { t, i18n } = useTranslation();
	const navigate = useNavigate();
	const current = normalizeLocaleValue(i18n.resolvedLanguage || i18n.language);
	return (
		<div style={{ background: '#f5f5f5', minHeight: '100vh' }}>
			<NativeHeader title={t('native.settings.language_setting')} onBack={() => navigate(-1)} />
			<div style={{ background: '#fff', borderTop: '10px solid #f5f5f5' }}>
				{LOCALE_OPTIONS.map((option) => (
					<NativeLanguageRow
						key={option.value}
						label={option.nativeLabel}
						active={current === option.value}
						onClick={() => {
							try { localStorage.setItem('i18nextLng', option.value); } catch { /* ignore */ }
							void i18n.changeLanguage(option.value);
						}}
					/>
				))}
			</div>
		</div>
	);
}

function NativeLanguageRow({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
	return (
		<button
			type="button"
			onClick={onClick}
			style={{
				width: '100%',
				height: 54,
				border: 0,
				borderBottom: '1px solid #eeeeee',
				background: '#fff',
				padding: '0 16px',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'space-between',
				color: '#20242b',
				fontSize: 16,
				textAlign: 'start',
			}}
		>
			<span>{label}</span>
			{active ? <CheckOutlined style={{ color: '#ff8a34' }} /> : null}
		</button>
	);
}

export function NativeAboutSettings() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	return (
		<div style={{ background: '#f5f5f5', minHeight: '100vh' }}>
			<NativeHeader title={t('native.settings.about_us')} onBack={() => navigate(-1)} />
			<div style={{ background: '#fff', marginTop: 10, padding: '28px 16px', textAlign: 'center', color: '#20242b' }}>
				<div style={{ fontSize: 15, fontWeight: 500 }}>Ulucu</div>
				<div style={{ marginTop: 8, color: '#999', fontSize: 14 }}>v{webPackage.version}</div>
			</div>
		</div>
	);
}

function NativeHeader({ title, onBack, action }: { title: string; onBack: () => void; action?: string }) {
	const { t } = useTranslation();
	return (
		<header style={{ height: 48, background: '#fff', borderBottom: '1px solid #eeeeee', display: 'grid', gridTemplateColumns: '56px minmax(0, 1fr) 56px', alignItems: 'center' }}>
			<button type="button" aria-label={t('common.back')} onClick={onBack} style={{ height: 48, border: 0, background: 'transparent', color: '#30343b', fontSize: 18 }}>
				<LeftOutlined />
			</button>
			<div style={{ textAlign: 'center', color: '#20242b', fontSize: 16, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
				{title}
			</div>
			<button type="button" style={{ height: 48, border: 0, background: 'transparent', color: '#ff8a34', fontSize: 15 }}>
				{action ?? ''}
			</button>
		</header>
	);
}

function NativeSettingRow({ title, trailing, onClick }: { title: string; trailing?: string; onClick: () => void }) {
	return (
		<button
			type="button"
			onClick={onClick}
			style={{
				width: '100%',
				height: 58,
				border: 0,
				borderBottom: '1px solid #eeeeee',
				background: '#fff',
				padding: '0 12px',
				display: 'flex',
				alignItems: 'center',
				gap: 12,
				textAlign: 'start',
				cursor: 'pointer',
			}}
		>
			<span style={{ width: 24, height: 24, background: '#ff8a34', borderRadius: 4, flex: '0 0 auto' }} />
			<span style={{ flex: 1, minWidth: 0, color: '#20242b', fontSize: 15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
				{title}
			</span>
			{trailing && <span style={{ color: '#999', fontSize: 14 }}>{trailing}</span>}
			<RightOutlined style={{ color: '#c8c8c8', fontSize: 14 }} />
		</button>
	);
}

function NativeInputRow({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
	return (
		<div style={{ height: 58, borderBottom: '1px solid #eeeeee', background: '#fff', padding: '0 12px', display: 'flex', alignItems: 'center' }}>
			<span style={{ minWidth: 142, color: '#20242b', fontSize: 14 }}>{label}</span>
			<input
				type="password"
				value={value}
				onChange={(event) => onChange(event.currentTarget.value)}
				style={{ flex: 1, minWidth: 0, border: 0, outline: 0, textAlign: 'right', fontSize: 14, background: 'transparent' }}
			/>
		</div>
	);
}

function estimateNativeCacheSize() {
	try {
		let bytes = 0;
		for (let i = 0; i < localStorage.length; i += 1) {
			const key = localStorage.key(i);
			if (!key || key.startsWith('auth')) continue;
			bytes += key.length + (localStorage.getItem(key)?.length ?? 0);
		}
		const kb = bytes / 1024;
		return `${kb.toFixed(kb >= 10 ? 2 : 1)}KB`;
	} catch {
		return '0KB';
	}
}
